import { API_BASE_URL, TOKEN_STORAGE_KEY } from '../config/index.js';

/**
 * Thin fetch wrapper around the backend API.
 *
 * - Attaches the stored Bearer token automatically.
 * - Unwraps the backend's `{ data }` envelope on success.
 * - Throws an Error carrying { status, details } on failure, so callers can
 *   `try/catch` and show err.message.
 */
class ApiService {
  async request(endpoint, { method = 'GET', body, headers } = {}) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);

    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });

    const isJson = res.headers.get('content-type')?.includes('application/json');
    const payload = isJson ? await res.json() : null;

    if (!res.ok) {
      const message = payload?.error?.message || `Request failed (${res.status})`;
      const error = new Error(message);
      error.status = res.status;
      error.details = payload?.error?.details;
      throw error;
    }

    return payload?.data ?? payload;
  }

  // ---- Auth ----
  register({ email, name, password }) {
    return this.request('/auth/register', { method: 'POST', body: { email, name, password } });
  }

  login({ email, password }) {
    return this.request('/auth/login', { method: 'POST', body: { email, password } });
  }

  /** Concern 9db69fc5 — one-click QA-demo sign-in; `role` is a fixed demo-role key. */
  demoLogin({ role }) {
    return this.request('/auth/demo-login', { method: 'POST', body: { role } });
  }

  me() {
    return this.request('/auth/me');
  }

  forgotPassword({ email }) {
    return this.request('/auth/forgot-password', { method: 'POST', body: { email } });
  }

  // ---- Users ----
  listUsers({ limit = 50, offset = 0, search } = {}) {
    const params = new URLSearchParams({ limit, offset });
    if (search) params.set('search', search);
    return this.request(`/users?${params.toString()}`);
  }

  updateUserCafeteriaScope(userId, cafeteriaId) {
    return this.request(`/users/${userId}/cafeteria-scope`, { method: 'PATCH', body: { cafeteriaId } });
  }

  // ---- User Management & RBAC ----
  listUsersDirectory({ search, role, status, page = 1, pageSize = 10 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (search) params.set('search', search);
    if (role) params.set('role', role);
    if (status) params.set('status', status);
    return this.requestWithMeta(`/users/directory?${params.toString()}`);
  }

  getUser(id) {
    return this.request(`/users/${id}`);
  }

  createUser(payload) {
    return this.request('/users', { method: 'POST', body: payload });
  }

  updateUser(id, payload) {
    return this.request(`/users/${id}`, { method: 'PUT', body: payload });
  }

  setUserStatus(id, status) {
    return this.request(`/users/${id}/status`, { method: 'PATCH', body: { status } });
  }

  resetUserPassword(id) {
    return this.request(`/users/${id}/reset-password`, { method: 'POST' });
  }

  // ---- Companies (minimal placeholder — full CRUD is CAF-03) ----
  listCompanies() {
    return this.request('/companies');
  }

  createCompany(payload) {
    return this.request('/companies', { method: 'POST', body: payload });
  }

  // ---- Client Companies & Contracts Management (CAF-03) ----
  listClientCompanies({ search, status, cafeteriaId, page = 1, pageSize = 15 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (search) params.set('search', search);
    if (status) params.set('status', status);
    if (cafeteriaId) params.set('cafeteriaId', cafeteriaId);
    return this.requestWithMeta(`/companies/directory?${params.toString()}`);
  }

  getClientCompany(id) {
    return this.request(`/companies/${id}`);
  }

  createClientCompany(payload) {
    return this.request('/companies/directory', { method: 'POST', body: payload });
  }

  updateClientCompany(id, payload) {
    return this.request(`/companies/${id}`, { method: 'PUT', body: payload });
  }

  // ---- Company Rate Cards & Meal Packs (CAF-04 — rate cards half only; meal packs is CAF-05) ----
  listRateCards({ search, companyId, page = 1, pageSize = 15 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (search) params.set('search', search);
    if (companyId) params.set('companyId', companyId);
    return this.requestWithMeta(`/rate-cards?${params.toString()}`);
  }

  getRateCard(id) {
    return this.request(`/rate-cards/${id}`);
  }

  createRateCard(payload) {
    return this.request('/rate-cards', { method: 'POST', body: payload });
  }

  updateRateCard(id, payload) {
    return this.request(`/rate-cards/${id}`, { method: 'PUT', body: payload });
  }

  cloneRateCard(id, payload) {
    return this.request(`/rate-cards/${id}/clone`, { method: 'POST', body: payload });
  }

  resolveRateCard({ companyId, date }) {
    const params = new URLSearchParams({ companyId, date });
    return this.request(`/rate-cards/resolve?${params.toString()}`);
  }

  // ---- Meal Packs (CAF-05 — the other half of the shared Rate Cards & Meal Packs page) ----
  listMealPacks({ search, companyId, status, page = 1, pageSize = 15 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (search) params.set('search', search);
    if (companyId) params.set('companyId', companyId);
    if (status) params.set('status', status);
    return this.requestWithMeta(`/meal-packs?${params.toString()}`);
  }

  getMealPack(id) {
    return this.request(`/meal-packs/${id}`);
  }

  createMealPack(payload) {
    return this.request('/meal-packs', { method: 'POST', body: payload });
  }

  updateMealPack(id, payload) {
    return this.request(`/meal-packs/${id}`, { method: 'PUT', body: payload });
  }

  /** Counter-scan-validates + records one redemption against a meal pack's per-daypart cap. */
  checkMealPackRedemption(mealPackId, { userId, daypart, mealSet }) {
    return this.request(`/meal-packs/${mealPackId}/redemption-check`, {
      method: 'POST',
      body: { userId, daypart, mealSet },
    });
  }

  // ---- Grace-Period Void (UC 87633dc9) ----
  /** "Select Recent Transaction to Void" candidates — the caller's own within-grace, still-approved scans. */
  listVoidableRedemptions() {
    return this.request('/meal-packs/redemptions/recent-voidable');
  }

  voidMealRedemption(redemptionId, { voidReason }) {
    return this.request(`/meal-packs/redemptions/${redemptionId}/void`, { method: 'POST', body: { voidReason } });
  }

  // ---- Self-Paid Add-On Extras (UC dc555297) ----
  /** The POS's Add-On Extras quick-pick catalog (Bottled Water, Soda, Fruit Cup, Chips, ...). */
  listAddonItems({ status = 'active', page = 1, pageSize = 100 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (status) params.set('status', status);
    return this.requestWithMeta(`/addon-items?${params.toString()}`);
  }

  /** Records the customer-paid extras against a just-served meal redemption — server resolves each
   * item's price from the active catalog; never sends a client-computed subtotal. */
  createAddonSale({ mealRedemptionId, tenderMethod, items }) {
    return this.request('/addon-sales', { method: 'POST', body: { mealRedemptionId, tenderMethod, items } });
  }

  // ---- Offline Queue & Auto-Sync (UC b58cba6c) ----
  /** Batch-applies queued offline scans (from the local encrypted vault) — verifies each item's
   * HMAC token server-side and applies it idempotently via its clientTxnId. Never rejects the
   * whole batch for one bad item; read `results[].status` per item. */
  batchSyncOfflineRedemptions(items) {
    return this.request('/meal-packs/redemptions/batch-sync', { method: 'POST', body: { items } });
  }

  // ---- Manager Exception Overrides & Void Audit (UC 286a38e6) ----
  listScanOverrides({ date, page = 1, pageSize = 15 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (date) params.set('date', date);
    return this.requestWithMeta(`/scan-overrides?${params.toString()}`);
  }

  authorizeScanOverride(payload) {
    return this.request('/scan-overrides', { method: 'POST', body: payload });
  }

  // ---- Meal Benefits Assignment & Entitlements (CAF-09) ----
  listEntitlementMealPackOptions() {
    return this.request('/entitlements/meal-pack-options');
  }

  listEntitlements({ search, mealPackId, department, page = 1, pageSize = 20 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (search) params.set('search', search);
    if (mealPackId) params.set('mealPackId', mealPackId);
    if (department) params.set('department', department);
    return this.requestWithMeta(`/entitlements?${params.toString()}`);
  }

  bulkAssignEntitlements(payload) {
    return this.request('/entitlements/bulk-assign', { method: 'POST', body: payload });
  }

  modifyEntitlementTier(id, payload) {
    return this.request(`/entitlements/${id}/modify-tier`, { method: 'PATCH', body: payload });
  }

  revokeEntitlement(id) {
    return this.request(`/entitlements/${id}/revoke`, { method: 'PATCH' });
  }

  // ---- Recipes (CAF-06's minimal Associated Recipe select) ----
  listRecipes() {
    return this.request('/recipes');
  }

  // ---- Ingredients & Recipe Master (CAF-02) — Ingredients half ----
  listIngredients({ search, storageCategory, status, page = 1, pageSize = 15 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (search) params.set('search', search);
    if (storageCategory) params.set('storageCategory', storageCategory);
    if (status) params.set('status', status);
    return this.requestWithMeta(`/ingredients?${params.toString()}`);
  }

  listIngredientOptions() {
    return this.request('/ingredients/options');
  }

  getIngredient(id) {
    return this.request(`/ingredients/${id}`);
  }

  createIngredient(payload) {
    return this.request('/ingredients', { method: 'POST', body: payload });
  }

  updateIngredient(id, payload) {
    return this.request(`/ingredients/${id}`, { method: 'PUT', body: payload });
  }

  // ---- Ingredients & Recipe Master (CAF-02) — Recipes & BOM half ----
  listRecipesDirectory({ search, page = 1, pageSize = 15 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (search) params.set('search', search);
    return this.requestWithMeta(`/recipes/directory?${params.toString()}`);
  }

  getRecipe(id) {
    return this.request(`/recipes/${id}`);
  }

  createRecipeWithBom(payload) {
    return this.request('/recipes', { method: 'POST', body: payload });
  }

  listRecipeBomVersions(recipeId) {
    return this.request(`/recipes/${recipeId}/bom-versions`);
  }

  createRecipeBomVersion(recipeId, payload) {
    return this.request(`/recipes/${recipeId}/bom-versions`, { method: 'POST', body: payload });
  }

  resolveRecipeBomVersion(recipeId, date) {
    const params = new URLSearchParams({ date });
    return this.request(`/recipes/${recipeId}/bom-versions/resolve?${params.toString()}`);
  }

  /** CAF-29 (UC 45d4e9ff-8359-4291-8650-98f498071f5e) — rejected 409 by the backend when the target
   * version is `readOnly` (KitchenOS-sourced); the caller surfaces that message inline. */
  deactivateRecipeBomVersion(recipeId, versionId) {
    return this.request(`/recipes/${recipeId}/bom-versions/${versionId}/deactivate`, { method: 'PATCH' });
  }

  // ---- Menu Items (CAF-06 — the "Menu Items" half of the Composer page) ----
  listMenuItems({ search, dietaryTag, status, page = 1, pageSize = 15 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (search) params.set('search', search);
    if (dietaryTag) params.set('dietaryTag', dietaryTag);
    if (status) params.set('status', status);
    return this.requestWithMeta(`/menu-items?${params.toString()}`);
  }

  listMenuItemOptions() {
    return this.request('/menu-items/options');
  }

  getMenuItem(id) {
    return this.request(`/menu-items/${id}`);
  }

  createMenuItem(payload) {
    return this.request('/menu-items', { method: 'POST', body: payload });
  }

  updateMenuItem(id, payload) {
    return this.request(`/menu-items/${id}`, { method: 'PUT', body: payload });
  }

  // ---- Meal Sets (CAF-06 — the "Meal Sets" half of the Composer page) ----
  listMealSets({ search, daypart, dietaryTag, status, page = 1, pageSize = 15 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (search) params.set('search', search);
    if (daypart) params.set('daypart', daypart);
    if (dietaryTag) params.set('dietaryTag', dietaryTag);
    if (status) params.set('status', status);
    return this.requestWithMeta(`/meal-sets?${params.toString()}`);
  }

  // Concern d70e6ca9 — unpaginated (bounded) active meal sets for selector/dropdown consumers
  // (the Cycle Menu Planner grid), mirroring listIngredientOptions/listMenuItemOptions.
  listActiveMealSets({ daypart } = {}) {
    const params = new URLSearchParams();
    if (daypart) params.set('daypart', daypart);
    const qs = params.toString();
    return this.request(`/meal-sets/options${qs ? `?${qs}` : ''}`);
  }

  getMealSet(id) {
    return this.request(`/meal-sets/${id}`);
  }

  createMealSet(payload) {
    return this.request('/meal-sets', { method: 'POST', body: payload });
  }

  updateMealSet(id, payload) {
    return this.request(`/meal-sets/${id}`, { method: 'PUT', body: payload });
  }

  duplicateMealSet(id, payload = {}) {
    return this.request(`/meal-sets/${id}/duplicate`, { method: 'POST', body: payload });
  }

  // ---- Cycle Menu Planner (CAF-07) ----
  listCycleMenus({ search, cafeteriaId, status, page = 1, pageSize = 15 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (search) params.set('search', search);
    if (cafeteriaId) params.set('cafeteriaId', cafeteriaId);
    if (status) params.set('status', status);
    return this.requestWithMeta(`/cycle-menus?${params.toString()}`);
  }

  getCycleMenu(id) {
    return this.request(`/cycle-menus/${id}`);
  }

  createCycleMenu(payload) {
    return this.request('/cycle-menus', { method: 'POST', body: payload });
  }

  updateCycleMenu(id, payload) {
    return this.request(`/cycle-menus/${id}`, { method: 'PUT', body: payload });
  }

  bulkAssignCycleMenuSlots(id, slots) {
    return this.request(`/cycle-menus/${id}/slots`, { method: 'POST', body: { slots } });
  }

  publishCycleMenu(id) {
    return this.request(`/cycle-menus/${id}/publish`, { method: 'POST' });
  }

  copyCycleMenu(id, payload) {
    return this.request(`/cycle-menus/${id}/copy`, { method: 'POST', body: payload });
  }

  /** Counter-POS contract — what meal set is scheduled for every enabled daypart at a cafeteria on a calendar date. */
  resolveCycleMenu({ cafeteriaId, date }) {
    const params = new URLSearchParams({ cafeteriaId, date });
    return this.request(`/cycle-menus/resolve?${params.toString()}`);
  }

  // ---- Attendance Pre-Orders & Headcount Confirmation (CAF-31) ----
  getAttendanceCompanyContext() {
    return this.request('/attendance/company-context');
  }

  listAttendanceConfirmations({ daypart, dispatchStatus, search, page = 1, pageSize = 20 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (daypart) params.set('daypart', daypart);
    if (dispatchStatus) params.set('dispatchStatus', dispatchStatus);
    if (search) params.set('search', search);
    return this.requestWithMeta(`/attendance/headcount-confirmations?${params.toString()}`);
  }

  submitAttendanceHeadcount(payload) {
    return this.request('/attendance/headcount-confirmations', { method: 'POST', body: payload });
  }

  editAttendanceHeadcount(id, payload) {
    return this.request(`/attendance/headcount-confirmations/${id}/edit-headcount`, { method: 'PATCH', body: payload });
  }

  /** Kitchen Production Forecast summary — aggregates confirmed headcounts across a service-date range. */
  getAttendanceKitchenForecast({ startDate, endDate }) {
    const params = new URLSearchParams({ startDate, endDate });
    return this.request(`/attendance/kitchen-forecast?${params.toString()}`);
  }

  // ---- Employee Web Credential QR (UC b8e49f1c) ----
  /**
   * The ONE server round trip the QR Credential Viewer makes — lazily issues (or returns the
   * already-issued) HMAC secret + Employee Info Summary Badge fields. Every QR refresh after
   * this is computed entirely client-side (Web Crypto), no further network call.
   */
  getCredentialQrSecret() {
    return this.request('/credentials/qr-secret');
  }

  // ---- Counter Scan & Validation POS (UC e2a14ded) ----
  /**
   * Recomputes + verifies the scanner input server-side (HMAC payload string, or a 6-char backup
   * code) and returns the employee's display profile + entitlement summary in one round trip.
   */
  validateCredentialToken(token) {
    return this.request('/credentials/validate', { method: 'POST', body: { token } });
  }

  // ---- KitchenOS Integration & Sync Management (CAF-28, UC 5144901e) ----
  getKitchenOsConnection() {
    return this.request('/kitchenos/connection');
  }

  saveKitchenOsConnection(payload) {
    return this.request('/kitchenos/connection', { method: 'PUT', body: payload });
  }

  /** GET /meta health check against the (possibly unsaved) base URL / key the operator is testing. */
  testKitchenOsConnection(payload) {
    return this.request('/kitchenos/connection/test', { method: 'POST', body: payload });
  }

  listKitchenOsSyncStreams() {
    return this.request('/kitchenos/sync-streams');
  }

  updateKitchenOsSyncStream(streamKey, enabled) {
    return this.request(`/kitchenos/sync-streams/${streamKey}`, { method: 'PATCH', body: { enabled } });
  }

  triggerKitchenOsSyncNow(streamKey) {
    return this.request(`/kitchenos/sync-streams/${streamKey}/sync-now`, { method: 'POST' });
  }

  triggerKitchenOsSyncAll() {
    return this.request('/kitchenos/sync-runs/sync-all', { method: 'POST' });
  }

  // Concern 5bd48374 — paginated (page/pageSize) so the Cafeteria Mapping tab can load
  // incrementally instead of rendering the whole roster (which crashed the renderer at 1,241+ test
  // cafeterias). Uses request() (not requestWithMeta()) since the payload is a compound object —
  // { items, kitchenOsOutlets, meta } — not a bare list.
  listKitchenOsCafeteriaMappings({ page = 1, pageSize = 50 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    return this.request(`/kitchenos/mappings/cafeterias?${params.toString()}`);
  }

  saveKitchenOsCafeteriaMapping(localCafeteriaId, kitchenOsOutletId) {
    return this.request(`/kitchenos/mappings/cafeterias/${localCafeteriaId}`, {
      method: 'PUT',
      body: { kitchenOsOutletId },
    });
  }

  listKitchenOsMenuItemMappings() {
    return this.request('/kitchenos/mappings/menu-items');
  }

  saveKitchenOsMenuItemMapping(localMenuItemId, kitchenOsFinishedGoodId) {
    return this.request(`/kitchenos/mappings/menu-items/${localMenuItemId}`, {
      method: 'PUT',
      body: { kitchenOsFinishedGoodId },
    });
  }

  listKitchenOsIngredientMappings() {
    return this.request('/kitchenos/mappings/ingredients');
  }

  saveKitchenOsIngredientMapping(localIngredientId, kitchenOsRawMaterialId) {
    return this.request(`/kitchenos/mappings/ingredients/${localIngredientId}`, {
      method: 'PUT',
      body: { kitchenOsRawMaterialId },
    });
  }

  listKitchenOsSyncRuns({ stream, status, dateFrom, dateTo, search, page = 1, pageSize = 20 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (stream) params.set('stream', stream);
    if (status) params.set('status', status);
    if (dateFrom) params.set('dateFrom', dateFrom);
    if (dateTo) params.set('dateTo', dateTo);
    if (search) params.set('search', search);
    return this.requestWithMeta(`/kitchenos/sync-runs?${params.toString()}`);
  }

  getKitchenOsSyncRunLog(runId) {
    return this.request(`/kitchenos/sync-runs/${runId}/log`);
  }

  // ---- Kitchen Batch Production Log & Wastage Tracking (UC 76f48d64) ----
  /** Daily Kitchen Production & Wastage Log — paginated, filterable by cafeteria/date/daypart/source and free-text search (Batch ID, Dish Name, or External Run ID). */
  listProductionBatches({ search, cafeteriaId, serviceDate, daypart, source, page = 1, pageSize = 20 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (search) params.set('search', search);
    if (cafeteriaId) params.set('cafeteriaId', cafeteriaId);
    if (serviceDate) params.set('serviceDate', serviceDate);
    if (daypart) params.set('daypart', daypart);
    if (source) params.set('source', source);
    return this.requestWithMeta(`/production-batches?${params.toString()}`);
  }

  /** Unbounded selector for the Wastage form's "Prepared Batch ID" dropdown — batches with remaining (unserved/unwasted) portions. */
  listProductionBatchOptions({ cafeteriaId, serviceDate } = {}) {
    const params = new URLSearchParams();
    if (cafeteriaId) params.set('cafeteriaId', cafeteriaId);
    if (serviceDate) params.set('serviceDate', serviceDate);
    const qs = params.toString();
    return this.request(`/production-batches/options${qs ? `?${qs}` : ''}`);
  }

  /** Native batch production entry — deducts raw ingredient inventory per the meal set's recipe BOMs. */
  createProductionBatch(payload) {
    return this.request('/production-batches', { method: 'POST', body: payload });
  }

  /** "View Ingredient Consumption Detail & BOM Line Mapping" row action — expected vs. actual usage per ingredient, with the >5% variance flag. */
  getProductionBatchBomConsumption(id) {
    return this.request(`/production-batches/${id}/bom-consumption`);
  }

  /** Unserved Meal & Wastage Log Form submit. */
  recordProductionWastage(batchId, payload) {
    return this.request(`/production-batches/${batchId}/wastage`, { method: 'POST', body: payload });
  }

  // ---- Variance & Meal Reconciliation Workspace (UC 780542e3) ----
  /** Sync/provenance banner + headcount-vs-production-vs-served chart + EOD reconciliation table for one service date (defaults to today). `cafeteriaId` is ignored server-side for a Cafeteria Manager (always their own scope); honored for Finance Admin / Operator Admin. */
  getReconciliationWorkspace({ cafeteriaId, serviceDate } = {}) {
    const params = new URLSearchParams();
    if (cafeteriaId) params.set('cafeteriaId', cafeteriaId);
    if (serviceDate) params.set('serviceDate', serviceDate);
    const qs = params.toString();
    return this.request(`/reconciliation/workspace${qs ? `?${qs}` : ''}`);
  }

  /** "Sign Off" row action — freezes one daypart slot's current numbers and locks it. */
  signOffReconciliation({ serviceDate, daypart, comment }) {
    return this.request('/reconciliation/sign-off', { method: 'POST', body: { serviceDate, daypart, comment } });
  }

  /** "EOD Sign-Off & Lock Reconciliation" top button — signs off every still-open daypart slot for the date. */
  signOffAllReconciliation({ serviceDate, comment }) {
    return this.request('/reconciliation/sign-off-all', { method: 'POST', body: { serviceDate, comment } });
  }

  /**
   * Ingredient-level trace for one reconciliation slot (Task 7, 2026-08-02-supply-to-order-traceability):
   * ordered-vs-prepared portions, per-ingredient expected/actual draw with measured-vs-theoretical basis,
   * count coverage, problems, and unmapped add-ons. `daypart` MUST be the lowercase five-value vocabulary
   * (breakfast, lunch, snacks, dinner, night_shift) — the workspace displays Title Case, so callers must
   * lowercase it before calling this.
   */
  getSupplyTrace({ cafeteriaId, serviceDate, daypart }) {
    const params = new URLSearchParams({ serviceDate, daypart });
    if (cafeteriaId) params.set('cafeteriaId', cafeteriaId);
    return this.request(`/supply-trace?${params.toString()}`);
  }

  // ---- Shift Stock Count capture (Task 5d, 2026-08-02-supply-to-order-traceability) ----
  /**
   * The count sheet — every active ingredient for one cafeteria/service date/daypart, with any
   * open/close counts already recorded. Cafeteria Manager only. `cafeteriaId` is optional and, if
   * sent, purely informational — the server always derives the effective scope from the caller's
   * own assigned cafeteria (null for an unscoped caller, who gets `{ unscoped: true }` back rather
   * than a 403 — see stock-count.controller.js).
   */
  getStockCounts({ cafeteriaId, serviceDate, daypart } = {}) {
    const params = new URLSearchParams({ serviceDate, daypart });
    if (cafeteriaId) params.set('cafeteriaId', cafeteriaId);
    return this.request(`/stock-counts?${params.toString()}`);
  }

  /** Record/correct ONE ingredient's count for one boundary (open or close) — per-row save, idempotent. */
  saveStockCount(payload) {
    return this.request('/stock-counts', { method: 'POST', body: payload });
  }

  // ---- RBAC authorization matrix + default landing page ----
  /** Caller-scoped menu grants (Concern e0c06adc) — every authenticated role, not admin-only. Powers AppNavbar's menu filtering; never pass a role, the server resolves it from the caller's own session. */
  /** The signed-in employee's own meal benefit — allowance, today's remaining, month-to-date usage. */
  getMyBenefit() {
    return this.request('/credentials/my-benefit');
  }

  getMyRbacMenu() {
    return this.request('/rbac/me');
  }

  getRbacMatrix(role) {
    return this.request(`/rbac/${encodeURIComponent(role)}`);
  }

  applyRbacMatrix(role, payload) {
    return this.request(`/rbac/${encodeURIComponent(role)}`, { method: 'PUT', body: payload });
  }

  resetRbacMatrix(role) {
    return this.request(`/rbac/${encodeURIComponent(role)}/reset`, { method: 'POST' });
  }

  listRoleLandingPages() {
    return this.request('/rbac/landing-pages');
  }

  // ---- Tenant & Cafeteria Management ----
  getTenantSettings() {
    return this.request('/tenant');
  }

  updateTenantSettings(payload) {
    return this.request('/tenant', { method: 'PUT', body: payload });
  }

  listCafeterias({ status, search, page = 1, pageSize = 10 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (status) params.set('status', status);
    if (search) params.set('search', search);
    return this.requestWithMeta(`/cafeterias?${params.toString()}`);
  }

  createCafeteria(payload) {
    return this.request('/cafeterias', { method: 'POST', body: payload });
  }

  updateCafeteria(id, payload) {
    return this.request(`/cafeterias/${id}`, { method: 'PUT', body: payload });
  }

  deactivateCafeteria(id) {
    return this.request(`/cafeterias/${id}/deactivate`, { method: 'PATCH' });
  }

  activateCafeteria(id) {
    return this.request(`/cafeterias/${id}/activate`, { method: 'PATCH' });
  }

  // ---- Employee Roster Management (CAF-08) ----
  listEmployees({ cafeteriaId, search, department, status, page = 1, pageSize = 20 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (cafeteriaId) params.set('cafeteriaId', cafeteriaId);
    if (search) params.set('search', search);
    if (department) params.set('department', department);
    if (status) params.set('status', status);
    return this.requestWithMeta(`/employees?${params.toString()}`);
  }

  listEmployeeDepartments() {
    return this.request('/employees/departments');
  }

  getEmployee(id) {
    return this.request(`/employees/${id}`);
  }

  createEmployee(payload) {
    return this.request('/employees', { method: 'POST', body: payload });
  }

  updateEmployee(id, payload) {
    return this.request(`/employees/${id}`, { method: 'PUT', body: payload });
  }

  revokeEmployeeQrAccess(id) {
    return this.request(`/employees/${id}/revoke-qr-access`, { method: 'PATCH' });
  }

  deactivateEmployee(id) {
    return this.request(`/employees/${id}/deactivate`, { method: 'PATCH' });
  }

  /** Multipart CSV bulk-import — file + actionMode + the site being imported for (+ optional columnMapping JSON string). */
  bulkImportEmployees({ file, actionMode, columnMapping, cafeteriaId }) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('actionMode', actionMode);
    if (cafeteriaId) formData.append('cafeteriaId', cafeteriaId);
    if (columnMapping) formData.append('columnMapping', JSON.stringify(columnMapping));
    return this.requestForm('/employees/bulk-import', formData);
  }

  /** Like request(), but sends FormData without forcing Content-Type: application/json (the browser sets the multipart boundary itself). */
  async requestForm(endpoint, formData) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    });

    const isJson = res.headers.get('content-type')?.includes('application/json');
    const payload = isJson ? await res.json() : null;

    if (!res.ok) {
      const message = payload?.error?.message || `Request failed (${res.status})`;
      const error = new Error(message);
      error.status = res.status;
      error.details = payload?.error?.details;
      throw error;
    }

    return payload?.data ?? payload;
  }

  // ---- Client Invoices & Billing (UC 3f3022a5) ----
  listInvoices({ search, status, companyId, instaPay, page = 1, pageSize = 15 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (search) params.set('search', search);
    if (status) params.set('status', status);
    if (companyId) params.set('companyId', companyId);
    if (instaPay) params.set('instaPay', instaPay);
    return this.requestWithMeta(`/invoices?${params.toString()}`);
  }

  getInvoice(id) {
    return this.request(`/invoices/${id}`);
  }

  generateInvoices(payload) {
    return this.request('/invoices/generate', { method: 'POST', body: payload });
  }

  recordInvoicePayment(id, payload) {
    return this.request(`/invoices/${id}/payments`, { method: 'POST', body: payload });
  }

  emailInvoice(id, payload) {
    return this.request(`/invoices/${id}/email`, { method: 'POST', body: payload });
  }

  // ---- Payment Collections & Overdue AR Tracking (UC 78ffc140) ----
  /** AR aging directory — one row per client company's outstanding balance, bucketed by days past due, plus the page's Key Metrics summary totals. */
  arAgingDirectory({ search, bucket, page = 1, pageSize = 15 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (search) params.set('search', search);
    if (bucket) params.set('bucket', bucket);
    return this.requestWithMetaAndSummary(`/invoices/ar-aging?${params.toString()}`);
  }

  /** "Outstanding Invoice" selector for the Payment Entry Form — reuses GET /invoices?status=Outstanding. */
  listOutstandingInvoices({ page = 1, pageSize = 100 } = {}) {
    return this.listInvoices({ status: 'Outstanding', page, pageSize });
  }

  recentCollections({ page = 1, pageSize = 10 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    return this.requestWithMeta(`/invoices/collections/recent?${params.toString()}`);
  }

  sendOverdueReminder(companyId) {
    return this.request(`/invoices/companies/${companyId}/send-overdue-reminder`, { method: 'POST' });
  }

  // ---- Credit Limit Rules & Lock Enforcement (UC 42af05e9) ----
  /** "Client Corporate Credit Health & Service Status" table — search + Warning/Suspended filter, paginated. */
  listCreditHealth({ search, serviceStatus, page = 1, pageSize = 15 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (search) params.set('search', search);
    if (serviceStatus) params.set('serviceStatus', serviceStatus);
    return this.requestWithMeta(`/credit-limits?${params.toString()}`);
  }

  getCreditPolicy(companyId) {
    return this.request(`/credit-limits/${companyId}`);
  }

  saveCreditPolicy(companyId, payload) {
    return this.request(`/credit-limits/${companyId}`, { method: 'PUT', body: payload });
  }

  grantCreditOverride(companyId, payload) {
    return this.request(`/credit-limits/${companyId}/override`, { method: 'POST', body: payload });
  }

  // ---- HR Consumption & Billing Statement Reports (UC 23fe252c) ----
  /** KPI cards + daily redemption trend + department cost allocation charts, scoped server-side to the caller's own company. */
  getHrReportSummary({ month, department, daypart } = {}) {
    const params = new URLSearchParams();
    if (month) params.set('month', month);
    if (department) params.set('department', department);
    if (daypart) params.set('daypart', daypart);
    const qs = params.toString();
    return this.request(`/hr-reports/summary${qs ? `?${qs}` : ''}`);
  }

  /** "Detailed Employee Redemption Audit Log" — paginated, filterable, searchable. */
  listHrReportRedemptions({ month, department, daypart, search, page = 1, pageSize = 20 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (month) params.set('month', month);
    if (department) params.set('department', department);
    if (daypart) params.set('daypart', daypart);
    if (search) params.set('search', search);
    return this.requestWithMeta(`/hr-reports/redemptions?${params.toString()}`);
  }

  /** Distinct departments for the caller's own company's roster — reused as-is from Employee Roster Management (CAF-08). */
  listHrReportDepartments() {
    return this.listEmployeeDepartments();
  }

  /** "Export Statement (CSV/PDF)" — streams the file and triggers a browser save-as, mirroring downloadInvoicePdf(). */
  async downloadHrStatement({ month, department, daypart, format = 'pdf' } = {}) {
    const params = new URLSearchParams({ format });
    if (month) params.set('month', month);
    if (department) params.set('department', department);
    if (daypart) params.set('daypart', daypart);
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/hr-reports/export?${params.toString()}`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!res.ok) {
      const isJson = res.headers.get('content-type')?.includes('application/json');
      const payload = isJson ? await res.json() : null;
      const message = payload?.error?.message || `Request failed (${res.status})`;
      const error = new Error(message);
      error.status = res.status;
      throw error;
    }
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `HR-Statement-${month || 'current'}.${format}`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  }

  // ---- Operator Analytics & System Dashboard (UC 007539f1) ----
  /** Executive KPI cards + multi-cafeteria volume trend, peak-hour density, and meal-set popularity charts — system-wide, Operator Admin only. */
  getOperatorAnalyticsDashboard({ startDate, endDate, cafeteriaId, daypart } = {}) {
    const params = new URLSearchParams();
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    if (cafeteriaId) params.set('cafeteriaId', cafeteriaId);
    if (daypart) params.set('daypart', daypart);
    const qs = params.toString();
    return this.request(`/operator-analytics/dashboard${qs ? `?${qs}` : ''}`);
  }

  /** "Export Executive Report" — streams the CSV summary and triggers a browser save-as, mirroring downloadHrStatement(). */
  async downloadOperatorAnalyticsReport({ startDate, endDate, cafeteriaId, daypart } = {}) {
    const params = new URLSearchParams({ format: 'csv' });
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    if (cafeteriaId) params.set('cafeteriaId', cafeteriaId);
    if (daypart) params.set('daypart', daypart);
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/operator-analytics/export?${params.toString()}`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!res.ok) {
      const isJson = res.headers.get('content-type')?.includes('application/json');
      const payload = isJson ? await res.json() : null;
      const message = payload?.error?.message || `Request failed (${res.status})`;
      const error = new Error(message);
      error.status = res.status;
      throw error;
    }
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `Operator-Analytics-${startDate || 'range'}_to_${endDate || 'current'}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  }

  // ---- Service Areas & Coverage Setup (CAF-31: UC 0a7dbc98-a090-448b-bc62-ebcdc13d8669) ----
  listServiceZones({ region, isActive, search, page = 1, pageSize = 10 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (region) params.set('region', region);
    if (isActive !== undefined && isActive !== null) params.set('isActive', isActive);
    if (search) params.set('search', search);
    return this.requestWithMeta(`/service-areas/zones?${params.toString()}`);
  }

  createServiceZone(payload) {
    return this.request('/service-areas/zones', { method: 'POST', body: payload });
  }

  updateServiceZone(id, payload) {
    return this.request(`/service-areas/zones/${id}`, { method: 'PUT', body: payload });
  }

  getServiceAreaSummary() {
    return this.request('/service-areas/summary');
  }

  listCoverageDemandLogs({ search, page = 1, pageSize = 10 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (search) params.set('search', search);
    return this.requestWithMeta(`/service-areas/demand-logs?${params.toString()}`);
  }

  checkServiceAreaCoverage(payload) {
    return this.request('/service-areas/coverage-check', { method: 'POST', body: payload });
  }

  /** "Export Demand CSV" — streams the out-of-coverage demand log and triggers a browser save-as, mirroring downloadOperatorAnalyticsReport(). */
  async downloadServiceAreaDemandCsv() {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/service-areas/demand-logs/export`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!res.ok) {
      const isJson = res.headers.get('content-type')?.includes('application/json');
      const payload = isJson ? await res.json() : null;
      const message = payload?.error?.message || `Request failed (${res.status})`;
      const error = new Error(message);
      error.status = res.status;
      throw error;
    }
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'Out-of-Coverage-Demand-Log.csv';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  }

  // ---- Accounting & ERP Ledger Sync (UC 1ce1dc5b) ----
  triggerAccountingSync(payload) {
    return this.request('/accounting/sync', { method: 'POST', body: payload });
  }

  listAccountingSyncRuns({ targetSystem, status, search, page = 1, pageSize = 20 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (targetSystem) params.set('targetSystem', targetSystem);
    if (status) params.set('status', status);
    if (search) params.set('search', search);
    return this.requestWithMeta(`/accounting/sync-runs?${params.toString()}`);
  }

  getAccountingSyncRun(runId) {
    return this.request(`/accounting/sync-runs/${runId}`);
  }

  retryAccountingSyncRun(runId) {
    return this.request(`/accounting/sync-runs/${runId}/retry`, { method: 'POST' });
  }

  /** Downloads the itemized PDF statement and triggers a browser save-as, mirroring the design's
   * "Print / Download PDF" action — the response is a raw application/pdf stream, not the { data }
   * JSON envelope, so this bypasses request()/requestWithMeta() and reads the blob directly. */
  async downloadInvoicePdf(id, filename) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/invoices/${id}/pdf`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!res.ok) {
      const isJson = res.headers.get('content-type')?.includes('application/json');
      const payload = isJson ? await res.json() : null;
      const message = payload?.error?.message || `Request failed (${res.status})`;
      const error = new Error(message);
      error.status = res.status;
      throw error;
    }
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename || `${id}.pdf`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  }

  /** Like request(), but returns { data, meta } instead of unwrapping to just data — for paginated endpoints. */
  async requestWithMeta(endpoint, options = {}) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: options.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      },
      ...(options.body ? { body: JSON.stringify(options.body) } : {}),
    });

    const isJson = res.headers.get('content-type')?.includes('application/json');
    const payload = isJson ? await res.json() : null;

    if (!res.ok) {
      const message = payload?.error?.message || `Request failed (${res.status})`;
      const error = new Error(message);
      error.status = res.status;
      error.details = payload?.error?.details;
      throw error;
    }

    return { data: payload?.data ?? [], meta: payload?.meta ?? null };
  }

  /** Like requestWithMeta(), but also passes through a top-level `summary` field (AR aging's Key Metrics totals). */
  async requestWithMetaAndSummary(endpoint, options = {}) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: options.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      },
      ...(options.body ? { body: JSON.stringify(options.body) } : {}),
    });

    const isJson = res.headers.get('content-type')?.includes('application/json');
    const payload = isJson ? await res.json() : null;

    if (!res.ok) {
      const message = payload?.error?.message || `Request failed (${res.status})`;
      const error = new Error(message);
      error.status = res.status;
      error.details = payload?.error?.details;
      throw error;
    }

    return { data: payload?.data ?? [], meta: payload?.meta ?? null, summary: payload?.summary ?? null };
  }

  // ---- Landing Page Manager (CAF-32: UC 757de86b-2900-4af6-aaab-b97f6e17817a) ----
  /** Operator Admin — draft + publish state. */
  getLandingPageConfig() {
    return this.request('/landing-page/config');
  }

  /** Operator Admin — saves the draft only (acceptance criterion #1, "changes preview live"). */
  updateLandingPageConfig(payload) {
    return this.request('/landing-page/config', { method: 'PUT', body: payload });
  }

  /** Operator Admin — publishes the current draft (acceptance criterion #2). */
  publishLandingPage() {
    return this.request('/landing-page/publish', { method: 'POST' });
  }

  unpublishLandingPage() {
    return this.request('/landing-page/unpublish', { method: 'POST' });
  }

  /** Anonymous — the public acquisition portal's published content. Never attaches a session even if one exists (a signed-in admin previewing the public page still sees exactly what an anonymous visitor sees). */
  async getPublicLandingPage() {
    const res = await fetch(`${API_BASE_URL}/landing-page/public`);
    const isJson = res.headers.get('content-type')?.includes('application/json');
    const payload = isJson ? await res.json() : null;
    if (!res.ok) {
      const message = payload?.error?.message || `Request failed (${res.status})`;
      const error = new Error(message);
      error.status = res.status;
      throw error;
    }
    return payload?.data ?? null;
  }

  /** Anonymous — the public landing page's "Check Service Coverage" form. Reuses the same coverage-check + inquiry-logging flow as the Service Areas admin page (service-areas/public/coverage-check), never attaching a session. */
  async checkPublicServiceAreaCoverage(payload) {
    const res = await fetch(`${API_BASE_URL}/service-areas/public/coverage-check`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const isJson = res.headers.get('content-type')?.includes('application/json');
    const responsePayload = isJson ? await res.json() : null;
    if (!res.ok) {
      const message = responsePayload?.error?.message || `Request failed (${res.status})`;
      const error = new Error(message);
      error.status = res.status;
      error.details = responsePayload?.error?.details;
      throw error;
    }
    return responsePayload?.data ?? null;
  }

  // ---- Lead Qualification & HR Invitation Workspace (CAF-34) ----
  listSignupRequestsForReview({ search, reviewStatus, serviceZone, page = 1, pageSize = 15 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (search) params.set('search', search);
    if (reviewStatus) params.set('reviewStatus', reviewStatus);
    if (serviceZone) params.set('serviceZone', serviceZone);
    return this.requestWithMeta(`/signup-requests?${params.toString()}`);
  }

  convertSignupRequest(id, payload) {
    return this.request(`/signup-requests/${id}/convert`, { method: 'POST', body: payload });
  }

  /** Anonymous — the Signup Request & Demand Intake Form's "Submit Inquiry" action (CAF-33, UC 46089101). Never attaches a session. */
  async submitSignupRequest(payload) {
    const res = await fetch(`${API_BASE_URL}/signup-requests`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const isJson = res.headers.get('content-type')?.includes('application/json');
    const responsePayload = isJson ? await res.json() : null;
    if (!res.ok) {
      const message = responsePayload?.error?.message || `Request failed (${res.status})`;
      const error = new Error(message);
      error.status = res.status;
      error.details = responsePayload?.error?.details;
      throw error;
    }
    return responsePayload?.data ?? null;
  }
}

export default new ApiService();
